import React from 'react';
import type {
  ExpandedSidebarGroupsState,
  TopbarOption,
  TopbarContextState,
  TopbarMenuKey,
} from './types';

const UI_STATE_STORAGE_KEY = 'seams-dashboard-ui-state-v1';
const DASHBOARD_SELECTED_CONTEXT_REPLACED_EVENT = 'dashboard:selected-context-replaced';

export const DASHBOARD_UI_QUERY_KEYS = {
  sidebarExpanded: 'db_sb',
  expandedGroups: 'db_groups',
  organization: 'db_org',
  project: 'db_project',
  environment: 'db_env',
  accountSettings: 'db_acct',
} as const;
const DASHBOARD_UI_QUERY_PARAM_KEYS = Object.values(DASHBOARD_UI_QUERY_KEYS);

type PersistedUiState<GroupKey extends string = string> = {
  isSidebarExpanded: boolean;
  expandedGroups: ExpandedSidebarGroupsState<GroupKey>;
  selectedContext: TopbarContextState;
};

type TopbarDropdownOptions = Record<TopbarMenuKey, TopbarOption[]>;

type UseDashboardUiPreferencesInput<GroupKey extends string> = {
  dropdownOptions: TopbarDropdownOptions;
  defaultContext: TopbarContextState;
  groupKeys: readonly GroupKey[];
  defaultExpandedGroups: ExpandedSidebarGroupsState<GroupKey>;
};

function isSelectableTopbarOption(option: TopbarOption): boolean {
  return option.disabled !== true;
}

function hasTopbarOption(
  menu: TopbarMenuKey,
  value: string,
  dropdownOptions: TopbarDropdownOptions,
): boolean {
  return dropdownOptions[menu].some(
    (option) => option.value === value && isSelectableTopbarOption(option),
  );
}

function resolveContextValue(
  menu: TopbarMenuKey,
  input: Partial<TopbarContextState> | undefined,
  dropdownOptions: TopbarDropdownOptions,
  defaults: TopbarContextState,
): string {
  const options = dropdownOptions[menu];
  if (menu === 'environment' && options.length === 0) return '';
  const rawValue = String(input?.[menu] || '').trim();
  if (rawValue && (options.length === 0 || hasTopbarOption(menu, rawValue, dropdownOptions))) {
    return rawValue;
  }
  const defaultValue = String(defaults[menu] || '').trim();
  if (
    defaultValue &&
    (options.length === 0 || hasTopbarOption(menu, defaultValue, dropdownOptions))
  ) {
    return defaultValue;
  }
  return String(options.find((option) => isSelectableTopbarOption(option))?.value || '').trim();
}

function sanitizeSelectedContext(
  input: Partial<TopbarContextState> | undefined,
  dropdownOptions: TopbarDropdownOptions,
  defaults: TopbarContextState,
): TopbarContextState {
  return {
    organization: resolveContextValue('organization', input, dropdownOptions, defaults),
    project: resolveContextValue('project', input, dropdownOptions, defaults),
    environment: resolveContextValue('environment', input, dropdownOptions, defaults),
    accountSettings: resolveContextValue('accountSettings', input, dropdownOptions, defaults),
  };
}

function areSelectedContextsEqual(left: TopbarContextState, right: TopbarContextState): boolean {
  return (
    left.organization === right.organization &&
    left.project === right.project &&
    left.environment === right.environment &&
    left.accountSettings === right.accountSettings
  );
}

function sanitizeExpandedGroups<GroupKey extends string>(
  input: Partial<ExpandedSidebarGroupsState<GroupKey>> | undefined,
  groupKeys: readonly GroupKey[],
  defaultExpandedGroups: ExpandedSidebarGroupsState<GroupKey>,
): ExpandedSidebarGroupsState<GroupKey> {
  const resolved = {} as ExpandedSidebarGroupsState<GroupKey>;
  for (const key of groupKeys) {
    resolved[key] = input?.[key] ?? defaultExpandedGroups[key];
  }
  return resolved;
}

function readExplicitSelectedContext(
  input: Partial<TopbarContextState> | undefined,
): Partial<TopbarContextState> {
  const organization = String(input?.organization || '').trim();
  const project = String(input?.project || '').trim();
  const environment = String(input?.environment || '').trim();
  const accountSettings = String(input?.accountSettings || '').trim();
  return {
    ...(organization ? { organization } : {}),
    ...(project ? { project } : {}),
    ...(environment ? { environment } : {}),
    ...(accountSettings ? { accountSettings } : {}),
  };
}

function readRawStoredState<GroupKey extends string = string>(): Partial<
  PersistedUiState<GroupKey>
> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(UI_STATE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Partial<PersistedUiState<GroupKey>>;
  } catch {
    return {};
  }
}

function readStoredState<GroupKey extends string>(
  dropdownOptions: TopbarDropdownOptions,
  defaultContext: TopbarContextState,
  groupKeys: readonly GroupKey[],
  defaultExpandedGroups: ExpandedSidebarGroupsState<GroupKey>,
): Partial<PersistedUiState<GroupKey>> {
  const parsed = readRawStoredState<GroupKey>();
  const explicitStoredContext = readExplicitSelectedContext(parsed.selectedContext);
  const sanitizedSelectedContext = sanitizeSelectedContext(
    parsed.selectedContext,
    dropdownOptions,
    defaultContext,
  );
  return {
    isSidebarExpanded:
      typeof parsed.isSidebarExpanded === 'boolean' ? parsed.isSidebarExpanded : undefined,
    expandedGroups: sanitizeExpandedGroups(parsed.expandedGroups, groupKeys, defaultExpandedGroups),
    // Preserve explicit stored selections even before dropdown options hydrate.
    selectedContext: {
      ...sanitizedSelectedContext,
      ...explicitStoredContext,
    },
  };
}

function parseExpandedGroupsParam<GroupKey extends string>(
  raw: string | null,
  groupKeys: readonly GroupKey[],
): Partial<ExpandedSidebarGroupsState<GroupKey>> | undefined {
  if (!raw) return undefined;
  const active = new Set(
    raw
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean),
  );
  const result: Partial<ExpandedSidebarGroupsState<GroupKey>> = {};
  for (const key of groupKeys) {
    result[key] = active.has(key);
  }
  return result;
}

function readUrlState<GroupKey extends string>(
  dropdownOptions: TopbarDropdownOptions,
  defaultContext: TopbarContextState,
  groupKeys: readonly GroupKey[],
  defaultExpandedGroups: ExpandedSidebarGroupsState<GroupKey>,
): Partial<PersistedUiState<GroupKey>> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);

  const sidebarExpandedRaw = params.get(DASHBOARD_UI_QUERY_KEYS.sidebarExpanded);
  const sidebarExpanded =
    sidebarExpandedRaw === '1' ? true : sidebarExpandedRaw === '0' ? false : undefined;
  const expandedGroupsRaw = params.get(DASHBOARD_UI_QUERY_KEYS.expandedGroups);
  const hasExpandedGroupOverride = expandedGroupsRaw !== null;
  const hasContextOverride =
    params.has(DASHBOARD_UI_QUERY_KEYS.organization) ||
    params.has(DASHBOARD_UI_QUERY_KEYS.project) ||
    params.has(DASHBOARD_UI_QUERY_KEYS.environment) ||
    params.has(DASHBOARD_UI_QUERY_KEYS.accountSettings);

  return {
    isSidebarExpanded: sidebarExpanded,
    ...(hasExpandedGroupOverride
      ? {
          expandedGroups: sanitizeExpandedGroups(
            parseExpandedGroupsParam(expandedGroupsRaw, groupKeys),
            groupKeys,
            defaultExpandedGroups,
          ),
        }
      : {}),
    ...(hasContextOverride
      ? {
          selectedContext: sanitizeSelectedContext(
            {
              organization: params.get(DASHBOARD_UI_QUERY_KEYS.organization) || undefined,
              project: params.get(DASHBOARD_UI_QUERY_KEYS.project) || undefined,
              environment: params.get(DASHBOARD_UI_QUERY_KEYS.environment) || undefined,
              accountSettings: params.get(DASHBOARD_UI_QUERY_KEYS.accountSettings) || undefined,
            },
            dropdownOptions,
            defaultContext,
          ),
        }
      : {}),
  };
}

function readUrlSelectedContext(
  dropdownOptions: TopbarDropdownOptions,
  defaultContext: TopbarContextState,
): TopbarContextState | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const hasContextOverride =
    params.has(DASHBOARD_UI_QUERY_KEYS.organization) ||
    params.has(DASHBOARD_UI_QUERY_KEYS.project) ||
    params.has(DASHBOARD_UI_QUERY_KEYS.environment) ||
    params.has(DASHBOARD_UI_QUERY_KEYS.accountSettings);
  if (!hasContextOverride) return null;
  const explicitContext = readExplicitSelectedContext({
    organization: params.get(DASHBOARD_UI_QUERY_KEYS.organization) || undefined,
    project: params.get(DASHBOARD_UI_QUERY_KEYS.project) || undefined,
    environment: params.get(DASHBOARD_UI_QUERY_KEYS.environment) || undefined,
    accountSettings: params.get(DASHBOARD_UI_QUERY_KEYS.accountSettings) || undefined,
  });

  const sanitized = sanitizeSelectedContext(
    {
      organization: params.get(DASHBOARD_UI_QUERY_KEYS.organization) || undefined,
      project: params.get(DASHBOARD_UI_QUERY_KEYS.project) || undefined,
      environment: params.get(DASHBOARD_UI_QUERY_KEYS.environment) || undefined,
      accountSettings: params.get(DASHBOARD_UI_QUERY_KEYS.accountSettings) || undefined,
    },
    dropdownOptions,
    defaultContext,
  );
  return {
    ...sanitized,
    // Preserve explicit context overrides from URL even before dropdown options hydrate.
    ...explicitContext,
  };
}

function readInitialState<GroupKey extends string>(
  dropdownOptions: TopbarDropdownOptions,
  defaultContext: TopbarContextState,
  groupKeys: readonly GroupKey[],
  defaultExpandedGroups: ExpandedSidebarGroupsState<GroupKey>,
): PersistedUiState<GroupKey> {
  const baseState: PersistedUiState<GroupKey> = {
    isSidebarExpanded: true,
    expandedGroups: { ...defaultExpandedGroups },
    selectedContext: sanitizeSelectedContext(undefined, dropdownOptions, defaultContext),
  };
  const stored = readStoredState(dropdownOptions, defaultContext, groupKeys, defaultExpandedGroups);
  const fromUrl = readUrlState(dropdownOptions, defaultContext, groupKeys, defaultExpandedGroups);
  const explicitStoredContext = readExplicitSelectedContext(stored.selectedContext);
  const explicitUrlContext = readExplicitSelectedContext(fromUrl.selectedContext);

  return {
    /* Opening the Console always starts with the rail open. Collapsing it is a
       working posture for the session you are in, not a preference to carry
       into the next one — a stored collapse used to greet you with a bare rail
       of icons on every later visit. An explicit ?db_sb= override still wins,
       since that is deliberate. */
    isSidebarExpanded: fromUrl.isSidebarExpanded ?? baseState.isSidebarExpanded,
    expandedGroups: sanitizeExpandedGroups(
      {
        ...baseState.expandedGroups,
        ...stored.expandedGroups,
        ...fromUrl.expandedGroups,
      },
      groupKeys,
      defaultExpandedGroups,
    ),
    selectedContext: {
      ...sanitizeSelectedContext(
        {
          ...baseState.selectedContext,
          ...stored.selectedContext,
          ...fromUrl.selectedContext,
        },
        dropdownOptions,
        defaultContext,
      ),
      ...explicitStoredContext,
      ...explicitUrlContext,
    },
  };
}

function writeStoredState(state: PersistedUiState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(UI_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function clearDashboardUiQueryState(pathname: string): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  let changed = false;
  if (params.has('onboarding')) {
    params.delete('onboarding');
    changed = true;
  }
  for (const key of DASHBOARD_UI_QUERY_PARAM_KEYS) {
    if (!params.has(key)) continue;
    params.delete(key);
    changed = true;
  }
  if (!changed) return;

  const nextSearch = params.toString();
  const nextUrl = `${pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextUrl !== currentUrl) {
    window.history.replaceState({}, '', nextUrl);
  }
}

function mergeSelectedContextState(
  current: Partial<TopbarContextState> | undefined,
  next: Partial<TopbarContextState>,
): TopbarContextState {
  const normalize = (value: unknown): string => String(value || '').trim();
  const hasSelectedContextKey = (key: keyof TopbarContextState): boolean =>
    Object.prototype.hasOwnProperty.call(next, key);
  const organization = hasSelectedContextKey('organization')
    ? normalize(next.organization)
    : normalize(current?.organization);
  // A project belongs to one organization and an environment to one project, so a
  // new organization or project drops the old child unless `next` names one.
  const project = hasSelectedContextKey('project')
    ? normalize(next.project)
    : organization === normalize(current?.organization)
      ? normalize(current?.project)
      : '';
  return {
    organization,
    project,
    environment: hasSelectedContextKey('environment')
      ? normalize(next.environment)
      : project === normalize(current?.project)
        ? normalize(current?.environment)
        : '',
    accountSettings: hasSelectedContextKey('accountSettings')
      ? normalize(next.accountSettings)
      : normalize(current?.accountSettings),
  };
}

export function clearDashboardUiState(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(UI_STATE_STORAGE_KEY);
  } catch {}
  clearDashboardUiQueryState(window.location.pathname || '/dashboard');
}

export function persistDashboardSelectedContext(
  selectedContext: Partial<TopbarContextState>,
): void {
  if (typeof window === 'undefined') return;
  const current = readRawStoredState();
  const nextSelectedContext = mergeSelectedContextState(current.selectedContext, selectedContext);
  const nextState: PersistedUiState = {
    isSidebarExpanded:
      typeof current.isSidebarExpanded === 'boolean' ? current.isSidebarExpanded : true,
    expandedGroups: current.expandedGroups || {},
    selectedContext: nextSelectedContext,
  };
  writeStoredState(nextState);
}

export function replaceDashboardSelectedContext(
  selectedContext: Partial<TopbarContextState>,
): void {
  if (typeof window === 'undefined') return;
  persistDashboardSelectedContext(selectedContext);
  window.dispatchEvent(
    new CustomEvent<Partial<TopbarContextState>>(DASHBOARD_SELECTED_CONTEXT_REPLACED_EVENT, {
      detail: selectedContext,
    }),
  );
}

export function readPersistedDashboardSidebarExpanded(): boolean {
  const current = readRawStoredState();
  return typeof current.isSidebarExpanded === 'boolean' ? current.isSidebarExpanded : true;
}

/** The rail state that a dashboard opens with: open, unless the URL asks for it closed. */
export function readInitialDashboardSidebarExpanded(): boolean {
  if (typeof window === 'undefined') return true;
  return (
    new URLSearchParams(window.location.search).get(DASHBOARD_UI_QUERY_KEYS.sidebarExpanded) !== '0'
  );
}

export function readPersistedDashboardSelectedContext(): Partial<TopbarContextState> {
  const current = readRawStoredState();
  const selected = current.selectedContext;
  if (!selected) return {};
  return readExplicitSelectedContext(selected);
}

/** The organization and project that the page URL names. Read it before the dashboard
    clears the URL parameters. */
export function readDashboardUrlContext(): Partial<
  Pick<TopbarContextState, 'organization' | 'project'>
> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  return readExplicitSelectedContext({
    organization: params.get(DASHBOARD_UI_QUERY_KEYS.organization) || undefined,
    project: params.get(DASHBOARD_UI_QUERY_KEYS.project) || undefined,
  });
}

type UseDashboardUiPreferencesResult<GroupKey extends string> = {
  isSidebarExpanded: boolean;
  expandedGroups: ExpandedSidebarGroupsState<GroupKey>;
  selectedContext: TopbarContextState;
  toggleSidebar: () => void;
  toggleGroup: (group: GroupKey) => void;
  onSelectContext: (menu: TopbarMenuKey, value: string) => void;
};

export function useDashboardUiPreferences<GroupKey extends string>(
  pathname: string,
  input: UseDashboardUiPreferencesInput<GroupKey>,
): UseDashboardUiPreferencesResult<GroupKey> {
  const { dropdownOptions, defaultContext, groupKeys, defaultExpandedGroups } = input;
  const initialState = React.useMemo(
    () => readInitialState(dropdownOptions, defaultContext, groupKeys, defaultExpandedGroups),
    [defaultContext, defaultExpandedGroups, dropdownOptions, groupKeys],
  );

  const [isSidebarExpanded, setIsSidebarExpanded] = React.useState<boolean>(
    initialState.isSidebarExpanded,
  );
  const [expandedGroups, setExpandedGroups] = React.useState<ExpandedSidebarGroupsState<GroupKey>>(
    initialState.expandedGroups,
  );
  const [selectedContext, setSelectedContext] = React.useState<TopbarContextState>(
    initialState.selectedContext,
  );
  const [preferencesHydrated, setPreferencesHydrated] = React.useState<boolean>(false);

  const toggleSidebar = React.useCallback(() => {
    setIsSidebarExpanded((current) => !current);
  }, []);

  const toggleGroup = React.useCallback((group: GroupKey) => {
    setExpandedGroups((current) => ({
      ...current,
      [group]: !current[group],
    }));
  }, []);

  const onSelectContext = React.useCallback(
    (menu: TopbarMenuKey, value: string) => {
      if (!hasTopbarOption(menu, value, dropdownOptions)) return;
      const next: Partial<TopbarContextState> = { [menu]: value };
      setSelectedContext((current) => mergeSelectedContextState(current, next));
    },
    [dropdownOptions],
  );

  React.useEffect(() => {
    setSelectedContext((current) => {
      const sanitized = sanitizeSelectedContext(current, dropdownOptions, defaultContext);
      return areSelectedContextsEqual(current, sanitized) ? current : sanitized;
    });
  }, [defaultContext, dropdownOptions]);

  React.useEffect(() => {
    const persisted = readPersistedDashboardSelectedContext();
    const organization = String(persisted.organization || '').trim();
    const project = String(persisted.project || '').trim();
    const environment = String(persisted.environment || '').trim();
    const accountSettings = String(persisted.accountSettings || '').trim();
    if (organization || project || environment || accountSettings) {
      setSelectedContext((current) => {
        const next: TopbarContextState = {
          ...current,
          ...(organization ? { organization } : {}),
          ...(project ? { project } : {}),
          ...(environment ? { environment } : {}),
          ...(accountSettings ? { accountSettings } : {}),
        };
        const sanitized = sanitizeSelectedContext(next, dropdownOptions, defaultContext);
        if (
          sanitized.organization === current.organization &&
          sanitized.project === current.project &&
          sanitized.environment === current.environment &&
          sanitized.accountSettings === current.accountSettings
        ) {
          return current;
        }
        return sanitized;
      });
    }
    setPreferencesHydrated(true);
  }, [defaultContext, dropdownOptions, pathname]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onSelectedContextReplaced = (event: Event) => {
      const custom = event as CustomEvent<Partial<TopbarContextState> | null>;
      const detail = custom.detail;
      if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return;
      setSelectedContext((current) => mergeSelectedContextState(current, detail));
    };
    window.addEventListener(
      DASHBOARD_SELECTED_CONTEXT_REPLACED_EVENT,
      onSelectedContextReplaced as EventListener,
    );
    return () => {
      window.removeEventListener(
        DASHBOARD_SELECTED_CONTEXT_REPLACED_EVENT,
        onSelectedContextReplaced as EventListener,
      );
    };
  }, []);

  React.useEffect(() => {
    const fromUrl = readUrlSelectedContext(dropdownOptions, defaultContext);
    if (!fromUrl) return;
    const sanitized = sanitizeSelectedContext(fromUrl, dropdownOptions, defaultContext);
    setSelectedContext((current) => {
      if (
        current.organization === sanitized.organization &&
        current.project === sanitized.project &&
        current.environment === sanitized.environment &&
        current.accountSettings === sanitized.accountSettings
      ) {
        return current;
      }
      return sanitized;
    });
  }, [defaultContext, dropdownOptions, pathname]);

  React.useLayoutEffect(() => {
    if (!preferencesHydrated) return;
    const nextState: PersistedUiState = {
      isSidebarExpanded,
      expandedGroups,
      selectedContext,
    };
    writeStoredState(nextState);
  }, [expandedGroups, isSidebarExpanded, preferencesHydrated, selectedContext]);

  React.useEffect(() => {
    clearDashboardUiQueryState(pathname);
  }, [pathname]);

  return {
    isSidebarExpanded,
    expandedGroups,
    selectedContext,
    toggleSidebar,
    toggleGroup,
    onSelectContext,
  };
}

export default useDashboardUiPreferences;
